/**
 * Buildings placed in the world.
 *
 * **The Urðarstígur block (worldgen 11).** 58 real OpenStreetMap outlines from
 * one bounding box, plus Flensborgarskóli — which the surface model *can* see,
 * kept from the original hand-placed pair because it is the counter-example to
 * everything in `Development/04_Can_The_DEM_See_A_Building.md`.
 *
 * A building here is three sources joined: **OSM** gives the outline, the
 * **HMS registry** (`lofi_geo_staging.structures`, D37) gives height, type,
 * year and material, and the **DEM** gives the ground to stand on. None of the
 * three is sufficient alone.
 *
 * ⚠️ **These are eaves heights on flat-topped boxes.** Every gabled house reads
 * about one roof short — Urðarstígur 10 stands 5 m where the architect's
 * drawings say ~7 m to the ridge. See `heightSource`.
 *
 * ⚠️ **23 of the 59 addresses have more registered buildings than we drew**, and
 * `matshlutar` counts them. **That is usually not a merged polygon — it is a
 * missing one.** In this block HMS holds **119 structures against OSM's 58**,
 * and **30 garages against OSM's 7**: roughly 23 garages exist in the register
 * that nobody has ever traced. Urðarstígur 10 is one — its OSM way is tagged
 * `building=detached` with no garage way at all, so the 93.4 m² outline is the
 * house's roof overhanging its ~69 m² registered footprint, *not* house plus
 * garage.
 *
 * ⚠️ **Found by haugur, looking at his own street, and it overturned the
 * opposite explanation stated confidently here first.** The registry has enough
 * to place these anyway — the garage is 7.00 × 4.50 m and 3.0 m tall — but not
 * their outline or which way they face. See T67.
 *
 * ⚠️ **The ground beneath an outline is flattened to `base`.** That is not a
 * nicety: over Flensborgarskóli the DEM already contains the building, reading
 * 31.8 m where the ground is 25.9. Raising a building on top of that would
 * stack the building on itself.
 *
 * Data lives in `data/buildings.json` so that adding a building is not a code
 * change (`Development/CLAUDE.md` rule 7).
 *
 * ⚠️ Outlines are **© OpenStreetMap contributors, ODbL 1.0** — attribution is an
 * obligation (T26).
 */

import buildingsData from '../data/buildings.json' with { type: 'json' };
import { blockIdForKey } from './blocks.ts';
import { northingToZ } from './world.ts';

export interface BuildingDef {
  readonly id: string;
  readonly name: string;
  /**
   * The OSM way the outline came from, so it can be traced back.
   *
   * ⚠️ **Absent means OSM does not have this building.** HMS knows about far
   * more than OSM has traced — 119 structures against 58 in the Urðarstígur
   * block, 30 garages against 7 — so a building can be real, registered and
   * dimensioned with nobody ever having drawn its outline. Those get a
   * `derivedFrom` instead. See T67.
   */
  readonly osmWayId?: string;
  /** How an outline was arrived at when it did not come from OSM. */
  readonly derivedFrom?: string;
  /** Ground level the building stands on, in metres above the ISN93 datum. */
  readonly base: number;
  /** Metres of building above {@link base}. */
  readonly height: number;
  /**
   * Where the height came from. Recorded per building because they are trusted
   * very differently, and a world built from four sources should be able to
   * say which one it used.
   *
   * ⚠️ **All of these are EAVES heights, not ridge heights.** A building here
   * is an extruded box with a flat top, so every gabled house reads about one
   * roof too short. That is a known, deliberate limitation of this pass.
   *
   * | | |
   * |---|---|
   * | `measured` | `structures.haed_maeld_m` — the HMS registry's own wall height. Best available |
   * | `storeys` | `haedir × 2.5 m`. 2.5 m is the Icelandic domestic standard (T48 §5) |
   * | `volume` | Gross volume ÷ gross floor area × storeys. Weakest of the three |
   * | `osm-levels` | `building:levels × 2.5 m`, where HMS had no match |
   * | `type-rule` | The original hand-placed pair, before `structures` existed |
   * | `default` | Nothing known. A flat 5 m so the building still exists |
   */
  readonly heightSource: 'measured' | 'storeys' | 'volume' | 'osm-levels' | 'type-rule' | 'default';
  /** Matshluti type — `Einbýlishús`, `Bílskúr/skúr`… HMS, where matched. */
  readonly tegund?: string;
  /** Registered use — `Íbúð`, `Bílskúr`… HMS, where matched. */
  readonly notkun?: string;
  readonly byggingarar?: number;
  /**
   * `Steypt`, `Timbur`, `St+timbur`, `Holsteinn`…
   *
   * ⚠️ **Carried but not yet used.** The palette has one building material, and
   * the only two candidate greys differ by about 2% — invisible in world. This
   * becomes useful the moment there is a timber block.
   */
  readonly byggingarefni?: string;
  /** Above-ground storeys. Excludes a kjallari — Urðarstígur 10 has three levels and reads 2. */
  readonly haedir?: number;
  /**
   * How many registered buildings HMS holds at this address.
   *
   * ⚠️ **More than 1 means this outline is standing in for several buildings**,
   * and it takes the height of the largest by volume. The intuitive reading —
   * that the polygon *merges* them — is usually **wrong**: far more often the
   * others simply have **no polygon at all**, because OSM never traced them.
   * In this block HMS holds 119 structures against OSM's 58, and 30 garages
   * against OSM's 7.
   */
  readonly matshlutar?: number;
  /** Outline area in m², from PostGIS. Urðarstígur 10: 93.4, against 95 on the 1993 site plan. */
  readonly osmAreaM2?: number;
  /**
   * Block keys for the three surfaces that carry a building.
   *
   * Absent means `steinsteypa` throughout — the plain grey massing every
   * building had before colour existed. Only buildings somebody has actually
   * looked at get these, so an uncoloured building is *visibly* uncoloured
   * rather than confidently wrong.
   */
  readonly wallBlock?: string;
  readonly roofBlock?: string;
  /** The visible base course. Icelandic houses sit on a concrete kjallari that reads darker. */
  readonly plinthBlock?: string;
  /**
   * Roof rise over run. `0.5` is a 27° pitch, `0.8` about 39°.
   *
   * Absent or 0 means a flat top — the extruded box every building was before
   * roofs existed. ⚠️ At 1 m per block a pitched roof arrives as **steps**,
   * because that is what a voxel roof is; Minecraft solves it with sub-block
   * stairs rather than smaller cubes (Q16). Expect a staircase, not a plane.
   */
  readonly roofPitch?: number;
  readonly note?: string;
  /** Outline in ISN93, easting then northing. Not closed — the last point joins the first. */
  readonly outline: readonly (readonly number[])[];
}

/**
 * A building with its outline in **world** coordinates, ready to test points
 * against.
 *
 * ⚠️ World z is the negated northing, so converting here rather than at every
 * use is what stops half the code working in one frame and half in the other.
 */
export interface Building {
  readonly def: BuildingDef;
  /** Flat `[x, z, x, z, …]` in world coordinates — the form the rasteriser wants. */
  readonly ring: Float64Array;
  /** Absolute y of the top of the WALL — the eaves. The roof stands on this. */
  readonly top: number;
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  /** Resolved block ids, so the rasteriser never looks anything up per column. */
  readonly wall: number;
  readonly roof: number;
  readonly plinth: number;
  /**
   * The ridge: a point on it, a unit direction along it, and how far the
   * furthest corner lies from it.
   *
   * ⚠️ **The ridge runs along the building's LONGEST edge**, which is what a
   * gable does on a rectangular house — the roof slopes off the two long sides.
   * Taking the shortest edge instead would put the ridge across the building
   * and every house would look like it had been turned ninety degrees.
   *
   * `halfSpan` is 0 when there is no roof, which is the flat-topped default.
   */
  readonly ridgeX: number;
  readonly ridgeZ: number;
  readonly ridgeDX: number;
  readonly ridgeDZ: number;
  readonly halfSpan: number;
  readonly pitch: number;
}

function toBuilding(def: BuildingDef): Building {
  const ring = new Float64Array(def.outline.length * 2);
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  def.outline.forEach((point, i) => {
    const x = point[0] ?? 0;
    const z = northingToZ(point[1] ?? 0);
    ring[i * 2] = x;
    ring[i * 2 + 1] = z;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  });

  /* The ridge runs along the longest edge of the outline, through the centroid
     of the vertices. Longest edge rather than a fitted axis because it is
     exactly what a builder does — the gable follows the long wall — and because
     it is stable: a fitted axis on an L-shaped plan can swing 90° when one wing
     is extended by a metre, and the roof would visibly jump. */
  let dx = 1;
  let dz = 0;
  let longest = -1;
  let cx = 0;
  let cz = 0;
  const n = def.outline.length;
  for (let i = 0; i < n; i++) {
    const ax = ring[i * 2] ?? 0;
    const az = ring[i * 2 + 1] ?? 0;
    const bx = ring[((i + 1) % n) * 2] ?? 0;
    const bz = ring[((i + 1) % n) * 2 + 1] ?? 0;
    cx += ax / n;
    cz += az / n;
    const ex = bx - ax;
    const ez = bz - az;
    const len = Math.hypot(ex, ez);
    if (len > longest) {
      longest = len;
      dx = ex / (len || 1);
      dz = ez / (len || 1);
    }
  }

  /* How far the outline reaches perpendicular to the ridge. The roof falls from
     the ridge to zero at that distance, so the eaves land on the wall rather
     than in mid-air or part-way up it. */
  let halfSpan = 0;
  for (let i = 0; i < n; i++) {
    const px = (ring[i * 2] ?? 0) - cx;
    const pz = (ring[i * 2 + 1] ?? 0) - cz;
    halfSpan = Math.max(halfSpan, Math.abs(px * dz - pz * dx));
  }

  const pitch = def.roofPitch ?? 0;

  /* `floor(base)` is the topmost ground block, matching worldgen's rule that
     you stand on top of it — so the walls start one metre above and the
     building is `height` blocks tall to the eaves. */
  return {
    def,
    ring,
    top: Math.floor(def.base) + def.height,
    minX,
    maxX,
    minZ,
    maxZ,
    wall: blockId(def.wallBlock),
    roof: blockId(def.roofBlock),
    plinth: blockId(def.plinthBlock),
    ridgeX: cx,
    ridgeZ: cz,
    ridgeDX: dx,
    ridgeDZ: dz,
    halfSpan: pitch > 0 ? halfSpan : 0,
    pitch,
  };
}

/**
 * A block key to its id, falling back to the plain grey massing material.
 *
 * ⚠️ **The fallback is deliberate and lives only here.** A building that nobody
 * has looked at, and a building whose colour key is misspelt, both come out
 * plain grey — visibly unfinished rather than confidently wrong.
 */
function blockId(key: string | undefined): number {
  return (key !== undefined ? blockIdForKey(key) : undefined) ?? blockIdForKey('steinsteypa') ?? 0;
}

/**
 * The absolute y of the roof surface over one column, given the wall top.
 *
 * Returns `top` unchanged where there is no roof, so a flat-topped building
 * needs no special case anywhere else.
 */
export function roofTopAt(b: Building, x: number, z: number, top: number): number {
  if (b.pitch <= 0) return top;
  const d = Math.abs((x - b.ridgeX) * b.ridgeDZ - (z - b.ridgeZ) * b.ridgeDX);
  return top + Math.round(Math.max(0, b.halfSpan - d) * b.pitch);
}

export const BUILDINGS: readonly Building[] = Object.freeze(
  (buildingsData.buildings as readonly BuildingDef[]).map(toBuilding),
);

/** The buildings whose bounding box overlaps a horizontal box, in world coordinates. */
export function buildingsIn(
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
): readonly Building[] {
  return BUILDINGS.filter(
    (b) => b.maxX >= minX && b.minX <= maxX && b.maxZ >= minZ && b.minZ <= maxZ,
  );
}
